/**
 * Utilidades de Performance y Core Web Vitals
 * Monitoreo y optimización de métricas clave
 */

interface PerformanceMetrics {
  FCP?: number; // First Contentful Paint
  LCP?: number; // Largest Contentful Paint
  FID?: number; // First Input Delay
  CLS?: number; // Cumulative Layout Shift
  TTFB?: number; // Time to First Byte
  TTI?: number; // Time to Interactive
}

/**
 * Medir Core Web Vitals
 */
export function measureWebVitals(): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) {
    return;
  }

  const metrics: PerformanceMetrics = {};

  // Largest Contentful Paint (LCP)
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { renderTime?: number; loadTime?: number };
      metrics.LCP = lastEntry.renderTime || lastEntry.loadTime || 0;
      
      if (metrics.LCP > 2500) {
        console.warn('⚠️ LCP lento:', metrics.LCP, 'ms (objetivo: <2500ms)');
      }
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
  } catch (e) {
    console.error('Error midiendo LCP:', e);
  }

  // First Input Delay (FID)
  try {
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const fidEntry = entry as PerformanceEntry & { processingStart?: number };
        metrics.FID = fidEntry.processingStart ? fidEntry.processingStart - entry.startTime : 0;
        
        if (metrics.FID > 100) {
          console.warn('⚠️ FID lento:', metrics.FID, 'ms (objetivo: <100ms)');
        }
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });
  } catch (e) {
    console.error('Error midiendo FID:', e);
  }

  // Cumulative Layout Shift (CLS)
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const layoutShiftEntry = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
        if (!layoutShiftEntry.hadRecentInput) {
          clsValue += layoutShiftEntry.value || 0;
        }
      });
      metrics.CLS = clsValue;
      
      if (metrics.CLS > 0.1) {
        console.warn('⚠️ CLS alto:', metrics.CLS, '(objetivo: <0.1)');
      }
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });
  } catch (e) {
    console.error('Error midiendo CLS:', e);
  }

  // First Contentful Paint (FCP)
  try {
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.name === 'first-contentful-paint') {
          metrics.FCP = entry.startTime;
          
          if (metrics.FCP > 1800) {
            console.warn('⚠️ FCP lento:', metrics.FCP, 'ms (objetivo: <1800ms)');
          }
        }
      });
    });
    fcpObserver.observe({ entryTypes: ['paint'] });
  } catch (e) {
    console.error('Error midiendo FCP:', e);
  }

  // Time to First Byte (TTFB)
  if (window.performance && window.performance.timing) {
    const timing = window.performance.timing;
    metrics.TTFB = timing.responseStart - timing.requestStart;
    
    if (metrics.TTFB > 600) {
      console.warn('⚠️ TTFB lento:', metrics.TTFB, 'ms (objetivo: <600ms)');
    }
  }

  // Log de métricas después de 5 segundos
  setTimeout(() => {
    console.log('📊 Core Web Vitals:', metrics);
  }, 5000);
}

/**
 * Optimizar imágenes con loading lazy y decoding async
 */
export function optimizeImages(): void {
  if (typeof document === 'undefined') return;

  const images = document.querySelectorAll('img:not([loading])');
  images.forEach((img) => {
    const htmlImg = img as HTMLImageElement;
    
    // Lazy loading para imágenes fuera del viewport
    if (!htmlImg.loading) {
      htmlImg.loading = 'lazy';
    }
    
    // Decoding asíncrono
    if (!htmlImg.decoding) {
      htmlImg.decoding = 'async';
    }
    
    // Añadir width y height si no existen (previene CLS)
    if (!htmlImg.width && !htmlImg.height) {
      const rect = htmlImg.getBoundingClientRect();
      if (rect.width && rect.height) {
        htmlImg.width = rect.width;
        htmlImg.height = rect.height;
      }
    }
  });
}

/**
 * Prefetch de rutas críticas
 */
export function prefetchCriticalRoutes(): void {
  if (typeof document === 'undefined') return;

  const criticalRoutes = [
    '/servicios/arquitectura-semantica',
    '/servicios/schema-markup-avanzado',
    '/servicios/seo-agentes-ia',
    '/blog'
  ];

  criticalRoutes.forEach((route) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = route;
    document.head.appendChild(link);
  });
}

/**
 * Reducir JavaScript no utilizado
 */
export function deferNonCriticalScripts(): void {
  if (typeof document === 'undefined') return;

  const scripts = document.querySelectorAll('script[src]:not([defer]):not([async])');
  scripts.forEach((script) => {
    const htmlScript = script as HTMLScriptElement;
    
    // Añadir defer a scripts no críticos
    if (!htmlScript.src.includes('critical') && !htmlScript.src.includes('inline')) {
      htmlScript.defer = true;
    }
  });
}

/**
 * Optimizar renderizado con requestIdleCallback
 */
export function scheduleNonCriticalWork(callback: () => void): void {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout: 2000 });
  } else {
    setTimeout(callback, 1);
  }
}

/**
 * Inicializar todas las optimizaciones de performance
 */
export function initPerformanceOptimizations(): void {
  // Medir Web Vitals
  measureWebVitals();
  
  // Optimizar imágenes
  scheduleNonCriticalWork(optimizeImages);
  
  // Prefetch de rutas críticas
  scheduleNonCriticalWork(prefetchCriticalRoutes);
  
  // Defer scripts no críticos
  scheduleNonCriticalWork(deferNonCriticalScripts);
}

/**
 * Resource Hints para mejorar carga
 */
export function addResourceHints(): void {
  if (typeof document === 'undefined') return;

  const hints = [
    { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
    { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
    { rel: 'dns-prefetch', href: 'https://cdn.jsdelivr.net' },
    { rel: 'dns-prefetch', href: 'https://readdy.ai' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com', crossOrigin: 'anonymous' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' }
  ];

  hints.forEach(({ rel, href, crossOrigin }) => {
    const existing = document.head.querySelector(`link[rel="${rel}"][href="${href}"]`);
    if (existing) return;

    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    if (crossOrigin) {
      link.crossOrigin = crossOrigin;
    }
    document.head.appendChild(link);
  });
}

export default {
  measureWebVitals,
  optimizeImages,
  prefetchCriticalRoutes,
  deferNonCriticalScripts,
  scheduleNonCriticalWork,
  initPerformanceOptimizations,
  addResourceHints
};
